'use client';

import React, { forwardRef, useState, useEffect, useRef } from 'react';
import { clsx } from 'clsx';
import { Icons, useScreenStore } from '@shared/ui';
import { type BasicShopUIPropTypes, type BuyerLayoutProps } from 'shop-ui/types';
import { useNavigationContext } from './useNavigation';
import { MenuGroup } from './MenuGroup';
import { MenuMobileGroup } from './MenuMobileGroup';
import { MenuItem } from './MenuItem';
import { MenuGroupItem } from './MenuGroupItem';
import { MenuSkeleton } from './MenuSkeleton';

interface MenuContentProps extends BasicShopUIPropTypes, BuyerLayoutProps {
    // 自行加入其他 props
    content: string;
}

export const MenuContent = forwardRef<HTMLDivElement, MenuContentProps>((props, ref) => {
    const { style, className, isEditingMode } = props;

    const {
        status: { isDesktop, isLaptop },
    } = useScreenStore();

    const { state, actions } = useNavigationContext();

    const listRef = useRef<HTMLUListElement | null>(null);

    const [referenceRef, setReferenceRef] = useState<HTMLElement | null>(null);

    useEffect(() => {
        let element: HTMLElement | HTMLUListElement | null = listRef.current;

        if (state.isHeaderNav) {
            element = document.querySelector('[data-section-header]');
        }

        if (state.isFooterNav) {
            element = document.querySelector('[data-section-footer]');
        }

        element && setReferenceRef(element);
    }, [state.isHeaderNav, state.isFooterNav]);

    return (
        <>
            <nav
                ref={ref}
                style={style}
                className={clsx('menu', className, {
                    isEditing: isEditingMode,
                })}
            >
                {state.isResponsiveType &&
                    state.isMenuLoaded &&
                    state.navigation?.next && (
                        <button
                            className="menu-hamburger"
                            onClick={actions.handleMobileMenuToggle}
                            aria-label="Menu"
                        >
                            {state.isMobileMenuOpen ? (
                                <Icons.csr.X size="24px" weight="bold" />
                            ) : (
                                <Icons.csr.List size="24px" weight="bold" />
                            )}
                        </button>
                    )}
                {!state.isMenuLoaded && <MenuSkeleton />}
                {state.isMenuLoaded && state.navigation?.next && (
                    <ul
                        ref={listRef}
                        className={clsx('menu-list', {
                            'menu-list--responsive': state.isResponsiveType,
                        })}
                    >
                        {state.isMenuLoaded &&
                            state.navigation.next.map((item) => (
                                <MenuItem
                                    key={`${item.value}-${item.name}`}
                                    {...item}
                                    showOpenInNewTab={state.openInNewTab}
                                >
                                    {item.name}
                                </MenuItem>
                            ))}
                    </ul>
                )}
                {!state.isMobileMenuOpen && (
                    <MenuGroup referenceRef={referenceRef}>
                        {state.navigationGroup.map((_, level) => (
                            <React.Fragment key={`level-${level}-menu-group`}>
                                <MenuGroupItem levelIndex={level} />
                                <div className="menu-group-divider" />
                            </React.Fragment>
                        ))}
                    </MenuGroup>
                )}
            </nav>
            {!isDesktop && !isLaptop && !state.isMenuLoaded && <MenuSkeleton />}
            {!isDesktop &&
                !isLaptop &&
                state.isMenuLoaded &&
                state.isMobileMenuOpen &&
                state.navigation?.next && (
                    <nav style={style} className="menu-mobile">
                        <MenuMobileGroup />
                    </nav>
                )}
        </>
    );
});

MenuContent.displayName = 'MenuContent';
